import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, getUnitLabel } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Package, User, CheckCircle, ArrowLeft, Folder, Hand, Truck } from "lucide-react";

/**
 * Quick-take a material via QR scan. Optimised for warehouse / on-site usage:
 *  - pre-fills the booker name from the logged-in user
 *  - one tap on a quantity quick-pick (1 / 5 / 10 / max) or manual entry
 *  - pickup is the default method (you're standing next to the pile)
 */
export default function ListingQuickbook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t, lang } = useI18n();
  const [listing, setListing] = useState(null);
  const [projects, setProjects] = useState([]);
  const [bokare, setBokare] = useState("");
  const [projectId, setProjectId] = useState("");
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState("pickup");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: ps }] = await Promise.all([
        api.get(`/listings/${id}`),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setListing(l);
      setProjects(ps || []);
    } catch {
      toast.error(t("detail.notFound"));
      navigate("/marketplace");
    }
  }, [id, navigate, t]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate(`/login?next=/listings/${id}/quickbook`); return; }
    setBokare(user.name || "");
    load();
  }, [loading, user, load, navigate, id]);

  if (loading || !user) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }
  if (!listing) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }

  const remaining = listing.quantity_remaining ?? listing.quantity ?? 0;
  const unitLabel = getUnitLabel(listing.unit, lang);
  const quickPicks = [1, 5, 10, Math.max(1, Math.floor(remaining))].filter((v, i, a) => v > 0 && a.indexOf(v) === i).slice(0, 4);

  const submit = async (e) => {
    e.preventDefault();
    const q = Number(qty);
    if (!q || q <= 0) {
      toast.error(t("lqb.qtyHint"));
      return;
    }
    if (q > remaining) {
      toast.error(t("lqb.qtyOver").replace("{max}", remaining).replace("{unit}", unitLabel));
      return;
    }
    if (user.user_id === listing.seller_id) {
      toast.error(t("detail.cantBookOwn"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        listing_id: listing.id,
        quantity: q,
        method,
        note: `Snabbtag via QR — ${bokare.trim() || user.name || ""}`.trim(),
      });
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("lqb.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <div className="max-w-[700px] mx-auto px-6 pt-16 pb-16 text-center" data-testid="listing-quickbook-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-[var(--moss)] text-[var(--bone)] grid place-items-center">
            <CheckCircle size={36} />
          </div>
          <h1 className="font-display text-3xl mt-6 tracking-tighter">{t("lqb.successTitle")}</h1>
          <p className="text-[var(--ink-700)] mt-3">
            {t("lqb.successBody")
              .replace("{qty}", String(done.quantity))
              .replace("{unit}", unitLabel)
              .replace("{title}", done.listing_title)}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={() => { setDone(null); setQty(1); }} className="btn-ghost" data-testid="listing-quickbook-again-btn">
              {t("lqb.bookAnother")}
            </button>
            <Link to={`/listings/${listing.id}`} className="btn-primary" data-testid="listing-quickbook-view-detail-btn">
              {t("lqb.viewDetail")}
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[700px] mx-auto px-6 pt-10 pb-16" data-testid="listing-quickbook-page">
        <Link to={`/listings/${listing.id}`} className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="listing-quickbook-back-link">
          <ArrowLeft size={12} /> {t("detail.back")}
        </Link>

        <div className="mt-4 flex items-start gap-4">
          <div className="w-14 h-14 bg-[var(--brick)] text-[var(--bone)] grid place-items-center shrink-0">
            <Package size={26} />
          </div>
          <div className="min-w-0">
            <div className="label-tech">{t("lqb.heading")}</div>
            <h1 className="font-display text-3xl tracking-tighter leading-tight truncate" data-testid="listing-quickbook-title">
              {listing.title}
            </h1>
            <div className="label-tech mt-1">{t("lqb.available").replace("{qty}", String(remaining)).replace("{unit}", unitLabel)}</div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div>
            <label className="label-tech">{t("lqb.bokare")}</label>
            <input
              type="text"
              value={bokare}
              onChange={(e) => setBokare(e.target.value)}
              className="input-tech mt-2"
              placeholder={t("lqb.bokarePh")}
              required
              data-testid="listing-quickbook-vem-input"
            />
          </div>

          <div>
            <label className="label-tech">{t("lqb.qty")}</label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              {quickPicks.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setQty(v)}
                  className={`p-3 border ${Number(qty) === v ? "border-[var(--brick)] bg-[var(--brick)] text-[var(--bone)]" : "border-[var(--ink-200)] bg-[var(--bone)]"} font-mono-tech text-sm`}
                  data-testid={`listing-quickbook-qty-${v}`}
                >
                  {v} {unitLabel}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="input-tech w-32"
                data-testid="listing-quickbook-qty-input"
              />
              <span className="label-tech">{unitLabel}</span>
            </div>
          </div>

          {projects.length > 0 && (
            <div>
              <label className="label-tech inline-flex items-center gap-1"><Folder size={12} />{t("lqb.project")}</label>
              <select
                className="input-tech mt-2"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                data-testid="listing-quickbook-project-select"
              >
                <option value="">{t("lqb.projectNone")}</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label-tech">{t("lqb.method")}</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMethod("pickup")}
                className={`p-3 border flex items-center justify-center gap-2 ${method === "pickup" ? "border-[var(--brick)] bg-[var(--brick)] text-[var(--bone)]" : "border-[var(--ink-200)] bg-[var(--bone)]"}`}
                data-testid="listing-quickbook-method-pickup"
              >
                <Hand size={14} /> {t("lqb.pickup")}
              </button>
              <button
                type="button"
                onClick={() => setMethod("delivery")}
                className={`p-3 border flex items-center justify-center gap-2 ${method === "delivery" ? "border-[var(--brick)] bg-[var(--brick)] text-[var(--bone)]" : "border-[var(--ink-200)] bg-[var(--bone)]"}`}
                data-testid="listing-quickbook-method-delivery"
              >
                <Truck size={14} /> {t("lqb.delivery")}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary w-full justify-center py-4"
            disabled={submitting}
            data-testid="listing-quickbook-submit"
          >
            <User size={14} /> {submitting ? t("lqb.submitting") : t("lqb.submit")}
          </button>
        </form>
      </div>
    </Layout>
  );
}
